import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Briefcase, ArrowLeft, Gift, Copy, CheckCircle2, Users, Share2, Star, Link2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Referrals() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [code, setCode] = useState('');
  const [redeemCode, setRedeemCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/referrals/my-stats`, { withCredentials: true });
      setStats(data);
      setCode(data.code || '');
    } catch (err) { console.error(err); }
  };

  const generateCode = async () => {
    try {
      const { data } = await axios.post(`${API}/referrals/generate`, {}, { withCredentials: true });
      setStats(prev => ({ ...prev, ...data }));
      setCode(data.code);
    } catch (err) { console.error(err); }
  };

  const copyLink = () => {
    const link = stats?.referral_link || `${window.location.origin}/auth?ref=${code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const redeemReferral = async () => {
    setError(''); setMessage('');
    try {
      const { data } = await axios.post(`${API}/referrals/redeem`, { code: redeemCode }, { withCredentials: true });
      setMessage(data.message);
      setRedeemCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to redeem code');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>

        {/* Hero */}
        <div className="bg-gradient-to-br from-[#A7F3D0]/30 to-[#FDFBF7] rounded-3xl p-8 mb-8 border border-[#047857]/10" data-testid="referral-hero">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-[#047857] flex items-center justify-center">
              <Gift size={22} className="text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-2xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>Refer a Friend</h1>
              <p className="text-sm text-[#1C2A24]/60">Both of you get 1 free month of Plus!</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8]">
            <div className="grid grid-cols-3 gap-4 text-center mb-6">
              <div>
                <Share2 size={20} className="text-[#047857] mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-xs text-[#1C2A24]/60 font-medium">Share your code</p>
              </div>
              <div>
                <Users size={20} className="text-[#D97757] mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-xs text-[#1C2A24]/60 font-medium">Friend signs up</p>
              </div>
              <div>
                <Star size={20} className="text-[#047857] mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-xs text-[#1C2A24]/60 font-medium">Both get Plus free!</p>
              </div>
            </div>
            <p className="text-sm text-[#1C2A24]/70 text-center">When your friend signs up and redeems your referral code, you <span className="font-semibold text-[#047857]">both</span> get upgraded to Plus membership for 1 free month. Unlimited applications, Quick Apply, and full community access!</p>
          </div>
        </div>

        {/* Your Referral Code */}
        <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 mb-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="referral-code-section">
          <h2 className="text-lg font-medium mb-4" style={{fontFamily:'Outfit'}}>Your Referral Code</h2>
          {code ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 bg-[#F4F1E8] rounded-2xl px-5 py-3 text-center">
                  <span className="text-2xl font-bold tracking-widest text-[#047857]" style={{fontFamily:'Outfit'}} data-testid="referral-code">{code}</span>
                </div>
                <Button onClick={copyLink} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white shrink-0" data-testid="copy-referral-btn">
                  {copied ? <><CheckCircle2 size={14} className="mr-1" /> Copied!</> : <><Copy size={14} className="mr-1" /> Copy Link</>}
                </Button>
              </div>
              <p className="text-xs text-[#1C2A24]/40 flex items-center gap-1"><Link2 size={12} /> {stats?.referral_link}</p>
            </>
          ) : (
            <Button onClick={generateCode} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="generate-code-btn">
              <Gift size={14} className="mr-2" /> Generate My Referral Code
            </Button>
          )}
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 gap-4 mb-6" data-testid="referral-stats">
            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 text-center">
              <p className="text-3xl font-semibold text-[#047857]" style={{fontFamily:'Outfit'}}>{stats.referrals_count}</p>
              <p className="text-xs text-[#1C2A24]/50">Friends Referred</p>
            </div>
            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 text-center">
              <p className="text-3xl font-semibold text-[#D97757]" style={{fontFamily:'Outfit'}}>{stats.credits_earned}</p>
              <p className="text-xs text-[#1C2A24]/50">Free Months Earned</p>
            </div>
          </div>
        )}

        {/* Referral History */}
        {stats?.history?.length > 0 && (
          <div className="bg-white rounded-3xl border border-[#E5E0D8] p-6 mb-6" data-testid="referral-history">
            <h3 className="text-sm font-medium mb-3" style={{fontFamily:'Outfit'}}>Referral History</h3>
            <div className="space-y-2">
              {stats.history.map(h => (
                <div key={h.id} className="flex items-center justify-between bg-[#F4F1E8]/50 rounded-xl p-3">
                  <div>
                    <p className="text-sm font-medium">{h.referred_name}</p>
                    <p className="text-xs text-[#1C2A24]/40">{new Date(h.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="tag-pill text-[10px]">{h.reward}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Redeem a Code */}
        <div className="bg-white rounded-3xl border border-[#E5E0D8] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="redeem-section">
          <h2 className="text-lg font-medium mb-4" style={{fontFamily:'Outfit'}}>Have a Referral Code?</h2>
          <div className="flex gap-3">
            <Input value={redeemCode} onChange={e => setRedeemCode(e.target.value.toUpperCase())} placeholder="Enter code (e.g. WFJ1A2B3C4D)"
              className="rounded-2xl bg-[#F4F1E8] border-[#E5E0D8] uppercase tracking-wider" data-testid="redeem-input" />
            <Button onClick={redeemReferral} className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white shrink-0" data-testid="redeem-btn">
              Redeem
            </Button>
          </div>
          {message && <p className="text-sm text-[#047857] mt-3 flex items-center gap-1" data-testid="redeem-success"><CheckCircle2 size={14} />{message}</p>}
          {error && <p className="text-sm text-red-500 mt-3" data-testid="redeem-error">{error}</p>}
        </div>
      </div>
    </div>
  );
}
